import React, { useEffect, useState } from 'react';
import { FormikProps, Formik } from 'formik';
import { Form, Select, Input } from 'formik-antd';
import { Row, Col } from 'antd';
import { isNil } from 'lodash';
import * as Yup from 'yup';

import {
  getAllPartners,
  getAllPartnersByMember,
} from 'redux/reducers/partnerSlice';
import Radio from 'components/formik/Radio';
import FormikEffect from 'components/formik/FormikEffect';
import FormItem from 'components/formik/FormItem';
import InputNumber from 'components/formik/InputNumber';
import PartnerSection from './PartnerSection';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import ContactsSection from 'components/modals/sections/ContactsSection';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { PartnerSplitEnum, partnerSplitOptions } from 'utils/assignmentUtils';
import { PartnerResponseModel, SplitTypeEnum } from 'api/backofficeApiClient';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { countryUSAid } from 'constants/index';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';

import styles from './SplitInfo.module.less';

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<SplitInfoFormValues>) => void;
  initialValues?: SplitInfoFormValues;
  memberId?: string;
};

interface PartnerData {
  splitAmount: number | null;
  splitLabel: PartnerSplitEnum;
  subcontractorRate?: number | null;
  country?: number;
  internationalStateName?: string;
  internationalCityName?: string;
  state?: number;
  city?: string;
  address?: string;
  zip?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface SplitInfoFormValues {
  splitType: SplitTypeEnum;

  // when SplitTypeEnum.ExistingPartner
  existingPartnerData?: { partner: string } & PartnerData;

  // when SplitTypeEnum.NewPartner
  newPartnerData?: { companyName: string } & PartnerData;
}

const splitInitialValidationSchema = {
  splitAmount: Yup.number()
    .nullable()
    .when('splitLabel', {
      is: PartnerSplitEnum.SplitPercent,
      then: Yup.number()
        .nullable()
        .min(0, 'Split amount % should be from 0 to 100')
        .max(100, 'Split amount % should be from 0 to 100'),
    })
    .when('splitLabel', {
      is: PartnerSplitEnum.PaymentPerHour,
      then: Yup.number()
        .nullable()
        .min(0, 'Payment per hour ($) should be from 0 to 100000')
        .max(100000, 'Payment per hour ($) should be from 0 to 100000'),
    }),
  splitLabel: Yup.number(),
};

const existingPartnerInitialValidationSchema = {
  partner: Yup.string(),
  splitLabel: splitInitialValidationSchema.splitLabel,
  splitAmount: splitInitialValidationSchema.splitAmount,
};

const newPartnerInitialValidationSchema = {
  companyName: Yup.string().max(
    120,
    'Company name should be 120 characters maximum',
  ),
  splitLabel: splitInitialValidationSchema.splitLabel,
  splitAmount: splitInitialValidationSchema.splitAmount,
  subcontractorRate: Yup.number()
    .nullable()
    .min(0, 'Subcontractor rate should be from 0 to 999999')
    .max(999999, 'Subcontractor rate should be from 0 to 999999'),
  country: Yup.number().nullable(),
  state: Yup.string().nullable().trim(),
  internationalStateName: Yup.string()
    .nullable()
    .trim()
    .max(120, `State should be 120 characters maximum`),
  city: Yup.string().trim().max(120, 'City should be 120 characters maximum'),
  internationalCityName: Yup.string()
    .nullable()
    .trim()
    .max(120, 'City should be 120 characters maximum'),
  address: Yup.string()
    .trim()
    .max(120, 'Address should be 120 characters maximum'),
  zip: Yup.string()
    .trim()
    .max(120, 'ZIP code should be 120 characters maximum'),
  firstName: Yup.string()
    .trim()
    .max(120, 'First name should be 120 characters maximum'),
  lastName: Yup.string()
    .trim()
    .max(120, 'Last name should be 120 characters maximum'),
  email: Yup.string()
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum'),
  phone: Yup.string()
    .trim()
    .max(120, 'Phone number should be 120 characters maximum'),
};

const initialValidationSchema = {
  splitType: Yup.number(),
  existingPartnerData: Yup.object().when('splitType', {
    is: (splitType: SplitTypeEnum) =>
      splitType === SplitTypeEnum.ExistingPartner,
    then: Yup.object({ ...existingPartnerInitialValidationSchema }),
  }),
  newPartnerData: Yup.object().when('splitType', {
    is: (splitType: SplitTypeEnum) => splitType === SplitTypeEnum.NewPartner,
    then: Yup.object({
      ...newPartnerInitialValidationSchema,
    }),
  }),
};

const submitValidationSchema = Yup.object({
  ...initialValidationSchema,
  splitType: initialValidationSchema.splitType.required('Field is required'),
  existingPartnerData: Yup.object().when('splitType', {
    is: (splitType: SplitTypeEnum) =>
      splitType === SplitTypeEnum.ExistingPartner,
    then: Yup.object({
      ...existingPartnerInitialValidationSchema,
      partner:
        existingPartnerInitialValidationSchema.partner.required(
          'Field is required',
        ),
      splitAmount:
        existingPartnerInitialValidationSchema.splitAmount.required(
          'Field is required',
        ),
      splitLabel:
        existingPartnerInitialValidationSchema.splitLabel.required(
          'Field is required',
        ),
    }),
  }),
  newPartnerData: Yup.object().when('splitType', {
    is: (splitType: SplitTypeEnum) => splitType === SplitTypeEnum.NewPartner,
    then: Yup.object({
      ...newPartnerInitialValidationSchema,
      companyName:
        newPartnerInitialValidationSchema.companyName.required(
          'Field is required',
        ),
      splitAmount:
        newPartnerInitialValidationSchema.splitAmount.required(
          'Field is required',
        ),
      splitLabel:
        newPartnerInitialValidationSchema.splitLabel.required(
          'Field is required',
        ),
      country:
        newPartnerInitialValidationSchema.country.required('Field is required'),
      internationalStateName:
        newPartnerInitialValidationSchema.internationalStateName.when(
          'country',
          {
            is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
            then: newPartnerInitialValidationSchema.internationalStateName.required(
              'Field is required',
            ),
          },
        ),
      state: newPartnerInitialValidationSchema.state.when('country', {
        is: (country: number) => checkIsCountriesWithStatesDropdown(country),
        then: newPartnerInitialValidationSchema.state.required(
          'Field is required',
        ),
      }),
      internationalCityName:
        newPartnerInitialValidationSchema.internationalCityName.when(
          'country',
          {
            is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
            then: newPartnerInitialValidationSchema.internationalCityName.required(
              'Field is required',
            ),
          },
        ),
      city: newPartnerInitialValidationSchema.state.when('country', {
        is: (country: number) => checkIsCountriesWithStatesDropdown(country),
        then: newPartnerInitialValidationSchema.city.required(
          'Field is required',
        ),
      }),
      address:
        newPartnerInitialValidationSchema.address.required('Field is required'),
      firstName:
        newPartnerInitialValidationSchema.firstName.required(
          'Field is required',
        ),
      lastName:
        newPartnerInitialValidationSchema.lastName.required(
          'Field is required',
        ),
      email:
        newPartnerInitialValidationSchema.email.required('Field is required'),
      phone:
        newPartnerInitialValidationSchema.phone.required('Field is required'),
    }),
  }),
});

export default React.memo((props: Props) => {
  const {
    formRef,
    onChange,
    initialValues: predefinedInitialValues,
    memberId,
  } = props;
  const dispatch = useAppDispatch();
  const isAdminRole = useAppSelector(isAdminSelector);
  const [partners, setPartners] = useState<PartnerResponseModel[]>([]);
  const [isPartnersLoading, setIsPartnersLoading] = useState(false);

  const initialValues: SplitInfoFormValues = {
    splitType: SplitTypeEnum.NoSplit,
    existingPartnerData: {
      partner: '',
      splitAmount: null,
      splitLabel: PartnerSplitEnum.SplitPercent,
    },
    newPartnerData: {
      companyName: '',
      splitAmount: null,
      splitLabel: PartnerSplitEnum.SplitPercent,
      subcontractorRate: null,
      country: countryUSAid,
      internationalStateName: undefined,
      state: undefined,
      internationalCityName: undefined,
      city: '',
      address: '',
      zip: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
  };

  useEffect(() => {
    loadPartners();
    // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    // in the existed partner we should prepopulate the data
    if (!predefinedInitialValues || !partners.length) return;

    const { splitType, existingPartnerData } = predefinedInitialValues;

    if (
      splitType !== SplitTypeEnum.ExistingPartner ||
      !existingPartnerData?.partner
    )
      return;

    handlePartnerChange(existingPartnerData?.partner, formRef?.current);
    // eslint-disable-next-line
  }, [partners, predefinedInitialValues]);

  const loadPartners = async () => {
    setIsPartnersLoading(true);
    const correctAction: any = isAdminRole
      ? getAllPartnersByMember
      : getAllPartners;

    const result = await dispatch(
      correctAction(isAdminRole ? memberId : undefined),
    );

    if (correctAction.fulfilled.match(result)) {
      setPartners(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
    setIsPartnersLoading(false);
  };

  const handlePartnerChange = (
    partnerId: string,
    formik: FormikProps<SplitInfoFormValues>,
  ) => {
    const selectedPartner = partners.find(
      (partner) => partner.id === partnerId,
    );

    if (!selectedPartner) return;

    const { primaryContact, generalInfo } =
      selectedPartner as PartnerResponseModel;

    formik.setFieldValue(
      'existingPartnerData.subcontractorRate',
      !isNil(generalInfo?.subcontractorRate)
        ? generalInfo?.subcontractorRate
        : null,
      false,
    );
    formik.setFieldValue(
      'existingPartnerData.country',
      generalInfo?.countryId || countryUSAid,
      false,
    );
    formik.setFieldValue(
      'existingPartnerData.state',
      generalInfo?.state,
      false,
    );
    formik.setFieldValue(
      'existingPartnerData.internationalStateName',
      generalInfo?.internationalStateName,
      false,
    );
    formik.setFieldValue(
      'existingPartnerData.internationalCityName',
      generalInfo?.internationalCityName,
      false,
    );
    formik.setFieldValue('existingPartnerData.city', generalInfo?.city, false);
    formik.setFieldValue(
      'existingPartnerData.address',
      generalInfo?.address,
      false,
    );
    formik.setFieldValue(
      'existingPartnerData.zip',
      generalInfo?.zipCode || '',
      false,
    );
    formik.setFieldValue(
      'existingPartnerData.firstName',
      primaryContact?.firstName,
      false,
    );
    formik.setFieldValue(
      'existingPartnerData.lastName',
      primaryContact?.lastName,
      false,
    );
    formik.setFieldValue(
      'existingPartnerData.email',
      primaryContact?.email,
      false,
    );
    formik.setFieldValue(
      'existingPartnerData.phone',
      primaryContact?.phoneNumber,
      false,
    );
  };

  return (
    <Formik
      innerRef={formRef}
      enableReinitialize
      validationSchema={submitValidationSchema}
      initialValues={predefinedInitialValues || initialValues}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <Row gutter={20} align="middle">
                <Col xs={24} md={6}>
                  <div className="modal-title">Split information</div>
                </Col>

                <Col xs={24} md={18}>
                  <FormItem name="splitType">
                    <Radio.Group name="splitType" className="radio-tab-view">
                      <Radio.Button value={SplitTypeEnum.NoSplit}>
                        No split
                      </Radio.Button>
                      <Radio.Button value={SplitTypeEnum.ExistingPartner}>
                        Existing partner
                      </Radio.Button>
                      <Radio.Button value={SplitTypeEnum.NewPartner}>
                        New partner
                      </Radio.Button>
                    </Radio.Group>
                  </FormItem>
                </Col>
              </Row>

              {formik.values.splitType === SplitTypeEnum.ExistingPartner && (
                <>
                  <Row gutter={40}>
                    <Col xs={24} md={12}>
                      <FormItem
                        name="existingPartnerData.partner"
                        label="Member split company *"
                      >
                        <FormikSelectWithLoading
                          isLoading={isPartnersLoading}
                          name="existingPartnerData.partner"
                          placeholder="Select partner split"
                          onChange={(partnerId) =>
                            handlePartnerChange(partnerId, formik)
                          }
                        >
                          {partners.map((partner) => (
                            <FormikSelectWithLoading.Option
                              key={`select-partner-${partner.id}`}
                              value={partner.id || -1}
                            >
                              {partner.generalInfo?.companyName}
                            </FormikSelectWithLoading.Option>
                          ))}
                        </FormikSelectWithLoading>
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <div className={styles.splitWrap}>
                        <div className={styles.splitLeft}>
                          <FormItem
                            label="Split amount *"
                            name="existingPartnerData.splitAmount"
                          >
                            <InputNumber
                              hideSpinners
                              name="existingPartnerData.splitAmount"
                              placeholder="Enter Split Amount"
                              className="full-width"
                              precision={2}
                            />
                          </FormItem>
                        </div>
                        <div className={styles.splitRight}>
                          <FormItem name="existingPartnerData.splitLabel">
                            <Select
                              name="existingPartnerData.splitLabel"
                              placeholder="Select Partner Split"
                            >
                              {partnerSplitOptions.map((split) => (
                                <Select.Option
                                  value={split.value}
                                  key={`partner-split-${split.value}`}
                                >
                                  {split.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </FormItem>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <PartnerSection
                    disabled
                    formNamePrefix="existingPartnerData"
                    formik={formik}
                  />

                  <ContactsSection
                    disabled
                    title="Primary contact"
                    formNamePrefix="existingPartnerData"
                    isTitleEnabled={false}
                  />
                </>
              )}

              {formik.values.splitType === SplitTypeEnum.NewPartner && (
                <>
                  <Row gutter={40}>
                    <Col xs={24} md={12}>
                      <FormItem
                        name="newPartnerData.companyName"
                        label="Company name *"
                      >
                        <Input
                          name="newPartnerData.companyName"
                          placeholder="Enter company name"
                          autoComplete='off'
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <div className={styles.splitWrap}>
                        <div className={styles.splitLeft}>
                          <FormItem
                            label="Split amount *"
                            name="newPartnerData.splitAmount"
                          >
                            <InputNumber
                              hideSpinners
                              name="newPartnerData.splitAmount"
                              placeholder="Enter Split Amount"
                              className="full-width"
                              precision={2}
                            />
                          </FormItem>
                        </div>
                        <div className={styles.splitRight}>
                          <FormItem name="newPartnerData.splitLabel">
                            <Select
                              name="newPartnerData.splitLabel"
                              placeholder="Select Partner Split"
                            >
                              {partnerSplitOptions.map((split) => (
                                <Select.Option
                                  value={split.value}
                                  key={`partner-split-${split.value}`}
                                >
                                  {split.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </FormItem>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <PartnerSection
                    formNamePrefix="newPartnerData"
                    formik={formik}
                  />

                  <ContactsSection
                    title="Primary contact"
                    formNamePrefix="newPartnerData"
                    isTitleEnabled={false}
                  />
                </>
              )}
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
});
